import { watch } from 'vue';
import { toValue } from '@vueuse/core';
import { useMutation } from '@composables/utils/useMutation';
import { ATRError, ATRUnknownError, ATRValidationError } from '../errors';
import { RegistryService } from '../services';

/**
 * * Add to Registry Payload
 * @param {import('@vueuse/core').MaybeRefOrGetter<AddToRegistryPayload>} payload - The payload to add to registry.
 * @param {Object} options - The options object.
 * @param {AddToRegistryValidator} [options.payloadValidator] - The payload validation function.
 * @param {((result: any) => void)} [options.onSuccess] - The callback function that will be called on success.
 * @param {((result: Error) => void)} [options.onError] - The callback function that will be called on error.
 * @param {(() => void)} [options.onBefore] - The callback function that will be called before the ATB action is triggered.
 * @param {((error: Error | null, result: any) => void)} [options.onAfter] -
 * The callback function that will be called after the ATB action is triggered.
 */
export function useAddToRegistry(payload, {
  payloadValidator = () => null,
  onSuccess = () => null,
  onError = () => null,
  onBefore = () => null,
  onAfter = () => null,
} = {}) {
  const addToRegistryService = new RegistryService();

  /**
   * * Adds Payload to registry (with validation)
   */
  const addToRegistry = async () => {
    const payloadValue = toValue(payload);
    const validationError = await payloadValidator(payloadValue);
    if (validationError) {
      throw new ATRValidationError(validationError);
    }

    try {
      return await addToRegistryService.addItem(payloadValue);
    } catch (error) {
      if (error instanceof Error || error instanceof TypeError) {
        throw new ATRError(error.message);
      }
      console.error(error); // eslint-disable-line no-console
      throw new ATRUnknownError('Unknown Error on Add to Registry');
    }
  };

  const addToRegistryMutation = useMutation(addToRegistry, {
    onSuccess,
    onError,
    onBefore,
    onAfter,
  });

  watch(addToRegistryMutation.isLoading, (newValue) => {
    if (!newValue) return;
    addToRegistryMutation.resetError();
  });

  watch(() => payload, addToRegistryMutation.resetError, { deep: true });

  return addToRegistryMutation;
}

export default useAddToRegistry;
